import { formatPackageDuration, quotePackageDuration } from './packageDuration';
import { formatPreferredDate } from './preferredDate';

export interface ShootSummaryInput {
  /** YYYY-MM-DD, or '' when still to be arranged. */
  date?: string;
  /** HH:MM, or '' when there is no preference. */
  time?: string;
  location?: { name?: string; address?: string; mapsUrl?: string } | null;
  /** True once Carmen has confirmed the date, so the wording drops "preferred". */
  confirmed?: boolean;
  /** Package length in minutes, stated with the shoot so the client knows how long it is. */
  durationMinutes?: number;
  /** Multi-day schedule when the quotation covers more than one session. */
  sessions?: Array<{
    id?: string;
    label?: string;
    date?: string;
    startTime?: string;
    durationMinutes?: number;
    location?: { name?: string; address?: string; mapsUrl?: string } | null;
  }>;
}

function coordinatePair(value: string | null): { lat: number; lon: number } | null {
  if (!value) return null;
  const match = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/.exec(value);
  if (!match) return null;
  const lat = Number(match[1]);
  const lon = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

/** A pin we can draw: coordinates stored on the Google Maps link from a chosen place. */
export function mapCoordinates(mapsUrl?: string | null): { lat: number; lon: number } | null {
  if (!mapsUrl) return null;
  let parsed: URL;
  try {
    parsed = new URL(mapsUrl);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  const fromQuery = coordinatePair(parsed.searchParams.get('query')) || coordinatePair(parsed.searchParams.get('q'));
  if (fromQuery) return fromQuery;
  const at = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(`${parsed.pathname}${parsed.search}`);
  return at ? coordinatePair(`${at[1]},${at[2]}`) : null;
}

/** A small Google map of a chosen pin. Built only from coordinates, never from a pasted address. */
export function mapPreviewUrl(mapsUrl?: string | null): string {
  const point = mapCoordinates(mapsUrl);
  if (!point) return '';
  return `https://www.google.com/maps?q=${point.lat},${point.lon}&z=16&output=embed`;
}

/** The link a client taps to find the place: the saved Google Maps link, or a Maps search for the address. */
export function shootMapsLink(location?: ShootSummaryInput['location']): string {
  if (!location) return '';
  if (location.mapsUrl && /^https:\/\//i.test(location.mapsUrl)) return location.mapsUrl;
  const query = [location.name, location.address].filter(Boolean).join(', ');
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
}

export function shootPlace(location?: ShootSummaryInput['location']): string {
  return location ? [location.name, location.address].filter(Boolean).join(', ') : '';
}

interface QuoteShootFields {
  preferredDate?: string;
  preferredTime?: string;
  preferredLocation?: ShootSummaryInput['location'];
  sessions?: ShootSummaryInput['sessions'];
  snapshot?: { lines?: Array<{ kind?: string; durationMinutes?: number }> } | null;
}

interface BookingShootFields {
  dateConfirmed?: boolean;
  shootDate?: string;
  startTime?: string;
  locationName?: string;
  address?: string;
  mapsUrl?: string;
  sessions?: Array<{
    id?: string;
    label?: string;
    shootDate?: string;
    startTime?: string;
    durationMinutes?: number;
    locationName?: string;
    address?: string;
    mapsUrl?: string;
  }>;
}

/**
 * What the client should be told about the shoot: the saved booking where there is one,
 * and what was asked for on the quotation otherwise.
 */
export function shootFromQuote(quote?: QuoteShootFields | null, booking?: BookingShootFields | null): ShootSummaryInput {
  const wanted = (quote && quote.preferredLocation) || {};
  // A booking Carmen has saved but not confirmed is private, so it never reaches a client message.
  const confirmed = Boolean(booking && booking.dateConfirmed && booking.shootDate);
  const row = confirmed && booking ? booking : {};
  const bookingSessions = confirmed && booking && Array.isArray(booking.sessions) && booking.sessions.length > 0
    ? booking.sessions.map((session) => ({
        id: session.id,
        label: session.label,
        date: session.shootDate || '',
        startTime: session.startTime || '',
        durationMinutes: session.durationMinutes,
        location: {
          name: session.locationName || '',
          address: session.address || '',
          mapsUrl: session.mapsUrl || '',
        },
      }))
    : [];
  const quoteSessions = Array.isArray(quote?.sessions) ? quote!.sessions! : [];
  const sessions = bookingSessions.length > 0 ? bookingSessions : quoteSessions;
  return {
    date: row.shootDate || (quote && quote.preferredDate) || '',
    time: row.startTime || (quote && quote.preferredTime) || '',
    location: {
      name: row.locationName || wanted.name || '',
      address: row.address || wanted.address || '',
      mapsUrl: row.mapsUrl || wanted.mapsUrl || '',
    },
    confirmed,
    durationMinutes: quotePackageDuration(quote) || undefined,
    ...(sessions.length > 0 ? { sessions } : {}),
  };
}

/**
 * The date, time and place as plain lines for any message that goes to the client.
 * A missing date or place is stated plainly, so the client always knows what is still open.
 */
function endClock(startTime?: string, durationMinutes?: number): string {
  const match = /^(\d{2}):(\d{2})$/.exec(String(startTime || ''));
  const minutes = Number(durationMinutes);
  if (!match || !Number.isFinite(minutes) || minutes <= 0) return '';
  const total = Number(match[1]) * 60 + Number(match[2]) + Math.round(minutes);
  const wrapped = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

function multiSessionLines(input: ShootSummaryInput): string[] {
  const sessions = (input.sessions || []).filter(Boolean);
  const heading = input.confirmed ? 'Schedule:' : 'Preferred schedule:';
  const lines = [heading];
  let totalMinutes = 0;
  sessions.forEach((session, index) => {
    const label = String(session.label || '').trim() || `Day ${index + 1}`;
    const date = formatPreferredDate(session.date) || 'date to be arranged';
    const start = session.startTime || '';
    const end = endClock(session.startTime, session.durationMinutes);
    const time = start ? (end ? `${start}-${end}` : start) : 'time to be agreed';
    const coverage = formatPackageDuration(session.durationMinutes);
    const place = shootPlace(session.location || input.location || null);
    lines.push([`${label}:`, date, time, coverage ? `(${coverage})` : '', place ? `· ${place}` : '']
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim());
    const minutes = Number(session.durationMinutes);
    if (Number.isFinite(minutes) && minutes > 0) totalMinutes += Math.round(minutes);
  });
  const total = formatPackageDuration(totalMinutes || input.durationMinutes);
  if (total) lines.push(`Total coverage: ${total}`);
  return lines;
}

export function shootSummaryLines(input: ShootSummaryInput): string[] {
  const sessions = Array.isArray(input.sessions) ? input.sessions.filter(Boolean) : [];
  if (sessions.length > 1) return multiSessionLines(input);
  const date = formatPreferredDate(input.date);
  const place = shootPlace(input.location);
  const map = shootMapsLink(input.location);
  const prefix = input.confirmed ? '' : 'Preferred ';
  const coverage = formatPackageDuration(input.durationMinutes);
  const lines = [
    `${prefix}${prefix ? 'shoot date' : 'Shoot date'}: ${date || 'to be arranged'}`,
    `${prefix}${prefix ? 'start time' : 'Start time'}: ${input.time || 'to be agreed'}`,
    ...(coverage ? [`Duration: ${coverage}`] : []),
    `Location: ${place || 'to be agreed'}`,
  ];
  if (map) lines.push(`Map: ${map}`);
  return lines;
}
